import React, { useState } from 'react'
import { Info } from '@entrata/portals-common/lib/icons'
import { ModalControl } from '@entrata/portals-common/lib/components'
import { RpModal } from 'portal-modules/common/components/modals'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import Button from 'portal-modules/common/components/Button'
import { PortalModal } from 'portal-modules/common/components/modals/portal-modal'
import { getEmbeddedViewFlag } from 'portal-modules/common/utils/get-embedded-flag'
import { connect } from 'react-redux'

const StyledDiv = styled('div')`
  font-size: 14px;
  padding: 0px 4px 16px;
  margin-bottom: 10px;
  color: ${({ theme }) =>
    `${getEmbeddedViewFlag() ? theme.primary.base : theme.gray.dark}`};
`

const StyledRpModal = styled(PortalModal)`
  max-width: 500px;
`

const StyledSpan = styled('span')`
  margin-left: 5px;
  vertical-align: middle;
  cursor: pointer;
`

const StyledButton = styled(Button)`
  margin-bottom: 16px;
`

const StyledTitle = styled('span')`
  color: ${({ theme }) =>
    `${getEmbeddedViewFlag() ? theme.gray.white : theme.gray.dark}`};
  font-size: 16px;
`

const PendingAutomaticAuthorization = ({
  onDelete,
  plaidStatusPendingOrFailed,
  marginRight = 0,
  showPopup = false,
  setShowPopup,
  isLeaseAndRentWordReplaced,
}) => {
  const [show, setShow] = useState(false)
  return (
    <ModalControl>
      {({ onClick, onClose, ref, ...props }) => (
        <>
          <span onClick={event => event.stopPropagation()}>
            <StyledSpan
              marginRight={marginRight}
              onClick={() => setShow(true)}
              ref={ref}
            >
              <Info size={14} />
            </StyledSpan>
          </span>
          <ContentModal
            {...props}
            plaidStatusPendingOrFailed={plaidStatusPendingOrFailed}
            isMediumDialog={
              plaidStatusPendingOrFailed !== 'failed_automatic_verification'
            }
            onDelete={onDelete}
            show={show || showPopup}
            setShow={setShow}
            setShowPopup={setShowPopup}
            isLeaseAndRentWordReplaced={isLeaseAndRentWordReplaced}
          />
        </>
      )}
    </ModalControl>
  )
}

export const ContentModal = ({
  plaidStatusPendingOrFailed,
  onDelete,
  show,
  setShow,
  setShowPopup,
  isLeaseAndRentWordReplaced = false,
  ...rest
}) => {
  const rentReplacedText = isLeaseAndRentWordReplaced ? 'housing fee' : 'rent'

  const getModalTitle = () => {
    switch (plaidStatusPendingOrFailed) {
      case 'failed_automatic_verification':
        return __('Bank Account Authorization Failed')
      case 'failed_manual_verification':
        return __('Bank Account Authorization Failed')
      case 'pending_automatic_verification':
        return __('Bank Account Pending Authorization Popup')
      case 'account_deleted_or_obsolete':
        return __('Invalid Account Number')
      case 'verification_expired':
        return __('Bank Account Verification Expired')
      case 'pending_manual_verification':
        return __('Bank Account Pending Your Authorization')
      case 'pending_screening_info_verification':
        return __('Account Holder Verification Required')
      case 'user_permission_revoked':
        return __('Account Access Revoked')
      case 'disable_flex_payment_account':
        return __('Flex Payment')
      case 'disable_bacs_payment_account':
        return __('Bacs Mandate Pending')
      default:
        return __('Bank Account Pending Automatic Authorization')
    }
  }

  const getModalBodyContent = () => {
    switch (plaidStatusPendingOrFailed) {
      case 'failed_automatic_verification':
        return __(
          'We were unable to verify your bank account at this time. Please add a new payment method.',
        )
      case 'failed_manual_verification':
        return __(
          'We were unable to verify your bank account at this time. Please add a new payment method.',
        )
      case 'account_deleted_or_obsolete':
        return __(
          'This account is either updated by your bank or not in use. Please, delete this account and add the new account.',
        )
      case 'verification_expired':
        return __(
          'This account is not eligible for update. Please, delete this account and add the new account.',
        )
      case 'pending_automatic_verification':
        return __(
          "We're currently working to verify your bank account. This generally takes about 24 hours. Once verified, you can come back and make your payment. No further action is needed at this time.",
        )
      case 'pending_manual_verification':
        return __(
          setShowPopup
            ? "Thank you! We're currently waiting on Plaid, our account verification provider, to make a $0.01 microdeposit to your account. This generally takes about 24 hours. Once the microdeposit settles to your account, you'll have to come back here and input the three digit code included at the front of the microdeposit transaction description to finish verifying your account. Then you'll be able to make your secure payment! No further action is needed until that time. Thank you."
            : "We're currently waiting on Plaid, our account verification provider, to make a $0.01 microdeposit to your account. This generally takes about 24 hours. Once the microdeposit settles to your account, you'll have to come back here and input the three digit code included at the front of the microdeposit transaction description to finish verifying your account. Then you'll be able to make your secure payment! No further action is needed until that time. Thank you.",
        )
      case 'pending_screening_info_verification':
        return __(
          'Please verify the account holder information to continue making payments from this account.',
        )
      case 'user_permission_revoked':
        return __(
          'Account access revoked. Please delete the account and re-add it to continue making payments.',
        )
      case 'disable_flex_payment_account':
        return __(
          'Flex allows you to split your {%s, 0} into smaller payments and pays your {%s, 0} in full on your behalf. <a href="https://getflex.com/split-rent" target="_blank">Learn</a> more or get <a href="https://help.getflex.com/hc/en-us" target="_blank">support</a>.',
          [rentReplacedText],
        )
      case 'disable_bacs_payment_account':
        return __(
          'The bacs mandate is in pending status. Once the mandate is active the account will be available to make payments.',
        )
      default:
        return __(
          "Thank you! We're currently working to verify your bank account. This generally takes about 24 hours. Once verified, you can come back and make your payment. No further action is needed at this time.",
        )
    }
  }

  return (
    show && (
      <>
        <StyledRpModal
          title={<StyledTitle>{getModalTitle()}</StyledTitle>}
          show={show}
          isForPlaid={true}
          {...rest}
        >
          <StyledDiv
            dangerouslySetInnerHTML={{ __html: getModalBodyContent() }}
          ></StyledDiv>
          {(plaidStatusPendingOrFailed === 'failed_automatic_verification' ||
            plaidStatusPendingOrFailed === 'failed_manual_verification') && (
            <StyledButton
              appearance='secondary'
              slim={false}
              onClick={() => {
                onDelete()
                setShow(false)
              }}
              fullWidth={true}
            >
              {__('Delete Account')}
            </StyledButton>
          )}
          <span onClick={event => event.stopPropagation()}>
            <Button
              appearance='primary'
              slim={false}
              size='large'
              onClick={() =>
                setShow(false) || (setShowPopup && setShowPopup(false))
              }
              fullWidth={true}
            >
              {(plaidStatusPendingOrFailed !==
                'failed_automatic_verification' &&
                plaidStatusPendingOrFailed !==
                  'pending_automatic_verification' &&
                plaidStatusPendingOrFailed !== 'pending_manual_verification' &&
                plaidStatusPendingOrFailed !==
                  'pending_screening_info_verification') ||
              (setShowPopup &&
                plaidStatusPendingOrFailed == 'pending_manual_verification')
                ? __('Continue')
                : __('Close')}
            </Button>
          </span>
        </StyledRpModal>
      </>
    )
  )
}

ContentModal.propTypes = {
  onDelete: PropTypes.func,
  plaidStatusPendingOrFailed: PropTypes.string,
  show: PropTypes.bool,
  showPopup: PropTypes.bool,
  setShow: PropTypes.func,
  isLeaseAndRentWordReplaced: PropTypes.bool,
}

PendingAutomaticAuthorization.propTypes = {
  onDelete: PropTypes.func,
  plaidStatusPendingOrFailed: PropTypes.string,
  marginRight: PropTypes.string,
  isLeaseAndRentWordReplaced: PropTypes.bool,
}

export default connect(({ productPermissions, propertyAssociation }) => ({
  isLeaseAndRentWordReplaced:
    productPermissions?.replace_lease_and_rent ||
    propertyAssociation?.resident_portal?.replace_lease_and_rent ||
    false,
}))(PendingAutomaticAuthorization)
